import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import React from "react";
import {useHistory} from "react-router";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {
    color,
    font,
    semanticColor,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingLarge,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";
import {getContinueUrl, parseUrl, useLocation} from "@khan/urls";

import caretRightIcon from "@phosphor-icons/core/regular/caret-right.svg";

type Props = {
    title: string;
    infoText?: string | React.ReactNode;
} & RolePickerButtonProps;

type RolePickerButtonProps = {
    learnerNextUrl: string;
    teacherNextUrl: string;
    parentNextUrl: string;
};

/**
 * RolePicker is a component that contains a title, role buttons, and optional
 * info text. This component is used in the sign-up flow to allow users to
 * indicate their role.
 *
 * NOTE: If you want just the buttons, use RolePickerButtons instead.
 */
export function RolePicker({
    title,
    learnerNextUrl,
    teacherNextUrl,
    parentNextUrl,
    infoText,
}: Props): React.ReactNode {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const noAccountErrorParam = queryParams.get("noAccount");
    return (
        <View style={{width: "100%"}}>
            <HeadingLarge style={{marginBottom: spacing.xLarge_32}}>
                {title}
            </HeadingLarge>
            <View id="no-account-error-message" aria-live="polite" role="alert">
                {noAccountErrorParam && (
                    <LabelSmall style={styles.errorMessage}>
                        {t`We cannot find an account associated with that login. Please sign up for an account to continue.`}
                    </LabelSmall>
                )}
            </View>
            <RolePickerButtons
                learnerNextUrl={learnerNextUrl}
                teacherNextUrl={teacherNextUrl}
                parentNextUrl={parentNextUrl}
            />
            {infoText && (
                <LabelMedium style={{padding: `${spacing.medium_16}px 0`}}>
                    {infoText}
                </LabelMedium>
            )}
        </View>
    );
}

// RolePickerButtons is a component that contains the role buttons.
// Upon clicking a button, a CEDAR event is fired with the current url path,
// and the user is navigated to the next page for that role.
function RolePickerButtons({
    learnerNextUrl,
    teacherNextUrl,
    parentNextUrl,
}: RolePickerButtonProps): React.ReactNode {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const history = useHistory();
    const location = useLocation();
    const continueUrl = getContinueUrl(location) ?? "/";
    const learnerWithContinueUrl = parseUrl(learnerNextUrl);
    const teacherWithContinueUrl = parseUrl(teacherNextUrl);
    const parentWithContinueUrl = parseUrl(parentNextUrl);
    if (continueUrl && continueUrl !== "/") {
        learnerWithContinueUrl.searchParams.set("continue", continueUrl);
        teacherWithContinueUrl.searchParams.set("continue", continueUrl);
        parentWithContinueUrl.searchParams.set("continue", continueUrl);
    }

    // Only show district admin button for US users
    const showDistrictAdminButton = RequestInfo.remoteCountry === "US";

    return (
        <View>
            {/* Learner Button */}
            <RoleButton
                href={
                    learnerWithContinueUrl.pathname +
                    learnerWithContinueUrl.search
                }
                safeWithNav={() =>
                    publishAnalyticsEvent({
                        eventType: "SignUpLearnerRoleClicked",
                        eventSchemaVersion: 1,
                        currentPageUrlPath: history.location.pathname,
                    })
                }
            >
                {t`I'm a learner`}
            </RoleButton>
            {/* Teacher Button */}
            <RoleButton
                href={
                    teacherWithContinueUrl.pathname +
                    teacherWithContinueUrl.search
                }
                safeWithNav={() =>
                    publishAnalyticsEvent({
                        eventType: "SignUpTeacherRoleClicked",
                        eventSchemaVersion: 1,
                        currentPageUrlPath: history.location.pathname,
                    })
                }
            >
                {t`I'm a teacher`}
            </RoleButton>
            {/* Parent Button */}
            <RoleButton
                href={
                    parentWithContinueUrl.pathname +
                    parentWithContinueUrl.search
                }
                safeWithNav={() =>
                    publishAnalyticsEvent({
                        eventType: "SignUpParentRoleClicked",
                        eventSchemaVersion: 1,
                        currentPageUrlPath: history.location.pathname,
                    })
                }
            >
                {t`I'm a parent`}
            </RoleButton>
            {/* District Admin Button */}
            {showDistrictAdminButton && (
                <RoleButton href="https://www.khanacademy.org/schools">
                    {t`I'm a district admin`}
                </RoleButton>
            )}
        </View>
    );
}

// RoleButton is a larger, lighter-blue button with a right caret icon used
// only in the role picker component.
function RoleButton({
    children,
    onClick,
    href,
    safeWithNav,
}: {
    href: string;
    onClick?: ((e: React.SyntheticEvent) => unknown) | undefined;
    children: string;
    safeWithNav?: (() => Promise<unknown>) | undefined;
}): React.ReactNode {
    return (
        <Button
            href={href}
            style={styles.roleButton}
            endIcon={caretRightIcon}
            onClick={onClick}
            safeWithNav={safeWithNav}
        >
            {children}
        </Button>
    );
}

const styles = StyleSheet.create({
    roleButton: {
        background: color.fadedBlue8,
        border: `1px solid ${color.fadedBlue}`,
        marginBottom: spacing.medium_16,
        color: color.activeBlue,
        fontSize: font.size.large,
        lineHeight: font.lineHeight.large,
        justifyContent: "space-between",
        height: 72,
        boxShadow: `0px 1px 4px 0px ${color.offBlack}29`,
        [":hover"]: {
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${color.activeBlue}`,
        },
        [":focus"]: {
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${color.activeBlue}`,
        },
    },
    errorMessage: {
        fontWeight: font.weight.regular,
        color: semanticColor.core.foreground.critical.default,
        marginBottom: spacing.medium_16,
    },
});
